import { supabase } from './supabase';
import { API_CONFIG, fetchAPI } from './api';
import type { Company, ContactsResponse } from '@/types';

type ContactsCacheEntry = {
  fetchedAt: number;
  contacts: ContactsResponse['contacts'];
  inFlight?: Promise<ContactsResponse['contacts']>;
};

const contactsCacheByUser = new Map<string, ContactsCacheEntry>();
const DEFAULT_CONTACTS_CACHE_MS = 20000;
const contactsCountCache = new Map<string, number>();

function contactsCountCacheKey(input: {
  userEmail: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}): string {
  return [
    String(input.userEmail || '').trim().toLowerCase(),
    String(input.search || '').trim().toLowerCase(),
    String(input.dateFrom || '').trim(),
    String(input.dateTo || '').trim(),
  ].join('|');
}

function normalizeDomain(value: string): string {
  const raw = value.trim().toLowerCase();
  if (!raw) return '';
  let working = raw.replace(/^mailto:/, '');
  if (working.includes('@')) {
    working = working.split('@').pop() || '';
  }
  working = working.replace(/^https?:\/\//, '').replace(/^www\./, '');
  working = working.split('/')[0] || '';
  working = working.split('#')[0] || '';
  working = working.split('?')[0] || '';
  if (working.includes(':')) {
    const parts = working.split(':');
    if (parts[0]) working = parts[0];
  }
  return working.trim();
}

function companyFromDomain(domainLike: string): string | null {
  const raw = normalizeDomain(domainLike);
  if (!raw) return null;
  const host = raw.split('@').pop() ?? raw;
  const label = host.split('.')[0] ?? '';
  if (!label) return null;
  return label
    .split(/[-_]+/)
    .map((p) => (p ? p[0]!.toUpperCase() + p.slice(1) : ''))
    .join(' ');
}

function normalizeTagArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item ?? '').trim()).filter(Boolean);
      }
    } catch {
      // Keep raw string fallback.
    }
    return [trimmed];
  }
  return [];
}

function extractContactListsFromTags(value: unknown): string[] {
  return normalizeTagArray(value).filter((tag) => tag.startsWith('Contact_'));
}

function mapSupabaseContactRow(r: any) {
  const tags = normalizeTagArray(r.tags);
  const legacyLists = normalizeTagArray(r.lists);
  const normalizedTypedLists = Array.from(
    new Set(
      [...extractContactListsFromTags(tags), ...extractContactListsFromTags(legacyLists)].map((entry) => String(entry || '').trim()).filter(Boolean)
    )
  );
  return {
    contactId: r.contact_id,
    id: r.contact_id,
    searchResultId: r.search_result_id || r.contact_id || r.id,
    searchResultIdRaw: r.search_result_id || '',
    fullName: r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim(),
    Name: r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim(),
    firstName: r.first_name,
    lastName: r.last_name,
    title: r.title,
    Title: r.title,
    Seniority: r.seniority,
    company: r.company,
    Company: r.company,
    CompanyDomain: r.company_domain,
    Website: r.company_domain,
    CompanyLIProfileUrl: r.company_li_profile_url,
    CompanyIndustry: r.company_industry,
    CompanyDescription: r.company_description,
    CompanyStaffCount: r.company_staff_count,
    CompanyStaffCountRange: r.company_staff_count_range,
    CompanyRevenueRange: r.company_revenue_range,
    Email: r.email1 || r.selected_email,
    email1: r.email1,
    email2: r.email2,
    email3: r.email3,
    email: r.email1 || r.selected_email,
    personalEmail1: r.personal_email,
    PersonalEmail: r.personal_email,
    contactPhone1: r.contact_phone1,
    companyPhone1: r.company_phone1,
    linkedIn: r.li_profile_url,
    LIProfileUrl: r.li_profile_url,
    image: r.image,
    ContactLocation: r.contact_location_full
      ? {
          city: r.contact_city,
          state: r.contact_state,
          stateAbbr: r.contact_state_abbr,
          countryAbbr: r.contact_country_abbr,
          full: r.contact_location_full,
        }
      : undefined,
    CompanyLocation: r.company_city
      ? {
          city: r.company_city,
          state: r.company_state,
          stateAbbr: r.company_state_abbr,
          countryAbbr: r.company_country_abbr,
        }
      : undefined,
    contact_city: r.contact_city,
    contact_state: r.contact_state,
    contact_state_abbr: r.contact_state_abbr,
    contact_country_abbr: r.contact_country_abbr,
    enrichedAt: r.enriched_at,
    enriched_at: r.enriched_at,
    researchedAt: r.enriched_at,
    Department: r.department,
    prospectStatus: r.prospect_status,
    tags,
    lists: normalizedTypedLists,
    _source: 'supabase',
  };
}

export async function loadAllContacts(
  userEmail: string,
  options?: { force?: boolean; cacheMs?: number; signal?: AbortSignal }
): Promise<ContactsResponse['contacts']> {
  const force = Boolean(options?.force);
  const signal = options?.signal;
  const cacheMs =
    Number.isFinite(Number(options?.cacheMs)) && Number(options?.cacheMs) >= 0
      ? Number(options?.cacheMs)
      : DEFAULT_CONTACTS_CACHE_MS;
  const now = Date.now();
  const cached = contactsCacheByUser.get(userEmail);
  if (!force && cached) {
    if (cached.inFlight) return cached.inFlight;
    if (now - cached.fetchedAt <= cacheMs) return cached.contacts;
  }

  const inFlight = (async () => {
  const contacts: ContactsResponse['contacts'] = [];
  let nextToken: string | null | undefined = null;

  do {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const url = new URL(`${API_CONFIG.RESEARCH_API}/contacts`);
    url.searchParams.set('userEmail', userEmail);
    url.searchParams.set('limit', '100');
    if (nextToken) {
      url.searchParams.set('nextToken', nextToken);
    }

    const response = await fetchAPI<ContactsResponse & { nextToken?: string | null }>(url.toString(), { signal });
    if (!response.success) {
      throw new Error('Failed to load contacts');
    }
    contacts.push(...(response.contacts || []));
    nextToken = response.nextToken;
  } while (nextToken);

    contactsCacheByUser.set(userEmail, {
      fetchedAt: Date.now(),
      contacts,
    });
  return contacts;
  })();

  contactsCacheByUser.set(userEmail, {
    fetchedAt: cached?.fetchedAt ?? 0,
    contacts: cached?.contacts ?? [],
    inFlight,
  });

  try {
    return await inFlight;
  } finally {
    const latest = contactsCacheByUser.get(userEmail);
    if (latest?.inFlight === inFlight) {
      contactsCacheByUser.set(userEmail, {
        fetchedAt: latest.fetchedAt,
        contacts: latest.contacts,
      });
    }
  }
}

export async function loadAllContactsPaged(
  userEmail: string,
  onUpdate: (contacts: ContactsResponse['contacts'], done: boolean) => void,
  pageSize = 25,
  options?: { view?: 'list' | 'full'; signal?: AbortSignal }
): Promise<ContactsResponse['contacts']> {
  const contacts: ContactsResponse['contacts'] = [];
  let nextToken: string | null | undefined = null;
  const view = options?.view;
  const signal = options?.signal;
  do {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const url = new URL(`${API_CONFIG.RESEARCH_API}/contacts`);
    url.searchParams.set('userEmail', userEmail);
    url.searchParams.set('limit', String(pageSize));
    if (view === 'list') {
      url.searchParams.set('view', 'list');
    }
    if (nextToken) {
      url.searchParams.set('nextToken', nextToken);
    }

    const response = await fetchAPI<ContactsResponse & { nextToken?: string | null }>(url.toString(), { signal });
    if (!response.success) {
      throw new Error('Failed to load contacts');
    }
    contacts.push(...(response.contacts || []));
    nextToken = response.nextToken;

    const done = !nextToken;
    onUpdate([...contacts], done);
  } while (nextToken);

  return contacts;
}

export async function loadContactsFromSupabase(
  userEmail: string,
): Promise<ContactsResponse['contacts']> {
  const PAGE_SIZE = 5000;
  let allContacts: ContactsResponse['contacts'] = [];
  let cursorTime: string | null = null;
  let cursorId: string | null = null;
  let done = false;


  while (!done) {
    const rpcParams: any = {
      p_user_email: userEmail,
      p_limit: PAGE_SIZE,
    };
    if (cursorTime && cursorId) {
      rpcParams.p_cursor_time = cursorTime;
      rpcParams.p_cursor_id = cursorId;
    }

    const { data, error } = await supabase.rpc('get_contacts_page_v2', rpcParams);

    if (error) {
      console.error('Supabase contacts query failed:', error);
      throw new Error(error.message);
    }

    const rows = data ?? [];

    const mapped = rows.map((r: any) => ({
      ...mapSupabaseContactRow(r),
      ContactLocation: r.contact_location_full
        ? {
            city: r.contact_city,
            state: r.contact_state,
            stateAbbr: r.contact_state_abbr,
            countryAbbr: r.contact_country_abbr,
            fullString: r.contact_location_full,
          }
        : undefined,
      CompanyLocation: {
        city: r.company_city,
        state: r.company_state,
        stateAbbr: r.company_state_abbr,
        countryAbbr: r.company_country_abbr,
        postCode: r.company_post_code,
        street1: r.company_street1,
      },
      researchedAt: r.enriched_at || r.created_at,
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      company_entity_id: r.company_entity_id,
    }));

    allContacts = allContacts.concat(mapped as any);
    if (rows.length < PAGE_SIZE) {
      done = true;
    } else {
      const lastRow = rows[rows.length - 1] as any;
      cursorTime = lastRow.enriched_at;
      cursorId = lastRow.contact_id;
      if (!cursorTime || !cursorId) done = true;
    }
  }

  return allContacts;
}

export async function loadContactItem(params: {
  userEmail: string;
  contactId?: string;
  searchResultId?: string;
}): Promise<Record<string, unknown>> {
  const userEmail = String(params.userEmail || '').trim();
  const contactId = String(params.contactId || '').trim();
  const searchResultId = String(params.searchResultId || '').trim();
  if (!userEmail) throw new Error('Missing userEmail');
  if (!contactId && !searchResultId) throw new Error('Missing contactId/searchResultId');

  const url = new URL(`${API_CONFIG.RESEARCH_API}/contacts/item`);
  url.searchParams.set('userEmail', userEmail);
  if (contactId) url.searchParams.set('contactId', contactId);
  if (searchResultId) url.searchParams.set('searchResultId', searchResultId);

  const response = await fetchAPI<{ success: boolean; contact?: Record<string, unknown>; error?: string }>(url.toString());
  if (!response.success || !response.contact) {
    throw new Error(response.error || 'Failed to load contact');
  }
  return response.contact;
}

// Load contacts from Research API and group by company
export async function loadCompanies(userEmail: string, options?: { signal?: AbortSignal }): Promise<Company[]> {
  const contacts = await loadAllContacts(userEmail, { signal: options?.signal });

  const companyMap: Record<string, Company> = {};

  const pickFirst = (...values: Array<string | undefined>) => {
    for (const v of values) {
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
  };

  const normalizeName = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  (contacts || []).forEach((contact) => {
    const raw = contact as Record<string, string | undefined>;
    const contactName =
      (contact.fullName ||
        contact.Name ||
        `${(raw as any).firstName || ''} ${(raw as any).lastName || ''}`.trim()).trim();
    const companyRaw = (contact.Company || contact.company || '').trim();
    const companyNorm = normalizeName(companyRaw);
    const contactNorm = normalizeName(contactName);

    const domain = normalizeDomain(
      raw.CompanyDomain || raw.Website || contact.email1 || contact.email || raw.Email || ''
    );
    const derivedCompany = companyFromDomain(domain) || '';

    const companyName =
      companyRaw && companyNorm && companyNorm !== contactNorm
        ? companyRaw
        : derivedCompany || companyRaw;
    if (!companyName) return;

    const companyId =
      'name:' +
      companyName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

    if (!companyMap[companyId]) {
      const facebook =
        raw.CompanyFacebookUrl ||
        (raw as any).CompanyFacebook ||
        (raw as any).CompanyFB ||
        (raw as any).CompanyFBUrl ||
        (raw as any).CompanyFacebookPage ||
        (raw as any).FacebookProfileUrl ||
        (raw as any).Facebook ||
        (raw as any).facebook ||
        (raw as any).companyFacebook ||
        (raw as any).companyFacebookUrl ||
        '';
      const location =
        pickFirst(
          raw.CompanyLocation,
          raw.CompanyCity,
          raw.CompanyState,
          raw.CompanyCountry,
          raw.Location,
          raw.location,
          raw.city,
          raw.state,
          raw.country,
          (raw as any).CompanyHQ,
          (raw as any).Headquarters,
          (raw as any).headquarters
        );
      const industry =
        pickFirst(
          raw.CompanyIndustry,
          raw.Industry,
          raw.industry,
          (raw as any).companyIndustry,
          (raw as any).industryPrimary
        );
      companyMap[companyId] = {
        companyId,
        companyName,
        contacts: [],
        phone: contact.companyPhone1 || contact.contactPhone1 || '',
        email: contact.email1 || contact.email || '',
        linkedin: contact.CompanyLIProfileUrl || '',
        facebook,
        glassdoorReviews: contact.employeeReviews || '',
        glassdoorJobs: contact.jobPostings || '',
        crunchbase: contact.crunchbaseUrl || '',
        companySize:
          contact.CompanyStaffCount || contact.CompanyStaffCountRange || '',
        website: normalizeDomain(
          raw.CompanyDomain ||
            raw.Website ||
            (raw as any).CompanyWebsite ||
            (raw as any).companyWebsite ||
            (raw as any).website ||
            (raw as any).companyDomain ||
            ''
        ),
        location,
        industry,
      };
    }

    companyMap[companyId].contacts.push({
      name: contact.fullName || contact.Name || '',
      title: contact.Title || contact.title || '',
      phone: contact.contactPhone1 || '',
      email: contact.email1 || contact.email || '',
    });
  });

  return Object.values(companyMap);
}

export async function updateContactRecord(params: {
  userEmail: string;
  contactId?: string;
  searchResultId?: string;
  updates?: Record<string, unknown>;
  unset?: string[];
}) {
  return fetchAPI<{ success: boolean; contact?: Record<string, unknown>; error?: string }>(
    `${API_CONFIG.RESEARCH_API}/contacts/update`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    }
  );
}

export async function loadContactsServerSide(
  userEmail: string,
  page: number = 0,
  perPage: number = 25,
  sortCol: string = 'enriched_at',
  sortDir: string = 'desc',
  search: string = '',
  dateFrom: string = '',
  dateTo: string = '',
  options?: {
    includeCount?: boolean;
    filters?: {
      hasBusinessEmail?: boolean;
      hasPersonalEmail?: boolean;
      hasLinkedinUrl?: boolean;
      hasBusinessPhone?: boolean;
      hasPersonalPhone?: boolean;
      titleTerms?: string[];
      titleExactMatch?: boolean;
      location?: string;
      locationScope?: 'both_or' | 'both_and' | 'contact' | 'company';
    };
  },
): Promise<{ contacts: any[]; totalCount: number }> {
  const filters = options?.filters;
  const hasAdvancedFilters =
    Boolean(filters?.hasBusinessEmail) ||
    Boolean(filters?.hasPersonalEmail) ||
    Boolean(filters?.hasLinkedinUrl) ||
    Boolean(filters?.hasBusinessPhone) ||
    Boolean(filters?.hasPersonalPhone) ||
    Boolean((filters?.titleTerms || []).length > 0) ||
    Boolean(String(filters?.location || '').trim());

  if (hasAdvancedFilters) {
    let query = supabase
      .from('seamless_data')
      .select('*', { count: options?.includeCount === false ? undefined : 'exact' })
      .eq('user_email', userEmail);

    if (search) {
      const term = search.trim();
      if (term) {
        query = query.or(
          [
            `full_name.ilike.%${term}%`,
            `company.ilike.%${term}%`,
            `title.ilike.%${term}%`,
          ].join(',')
        );
      }
    }
    if (dateFrom) query = query.gte('enriched_at', dateFrom);
    if (dateTo) query = query.lte('enriched_at', `${dateTo}T23:59:59.999Z`);

    if (filters?.hasBusinessEmail) {
      query = query.or('email1.not.is.null,selected_email.not.is.null');
    }
    if (filters?.hasPersonalEmail) {
      query = query.not('personal_email', 'is', null);
    }
    if (filters?.hasLinkedinUrl) {
      query = query.not('li_profile_url', 'is', null);
    }
    if (filters?.hasBusinessPhone) {
      query = query.or('contact_phone1.not.is.null,company_phone1.not.is.null');
    }
    if (filters?.hasPersonalPhone) {
      // Keep this conservative; some datasets store personal/mobile here.
      query = query.not('contact_phone1', 'is', null);
    }
    if ((filters?.titleTerms || []).length > 0) {
      const terms = (filters?.titleTerms || []).map((t) => t.trim()).filter(Boolean);
      if (terms.length > 0) {
        if (filters?.titleExactMatch) {
          query = query.in('title', terms);
        } else {
          query = query.or(terms.map((term) => `title.ilike.%${term}%`).join(','));
        }
      }
    }
    if (String(filters?.location || '').trim()) {
      const locationTerm = String(filters?.location || '').trim();
      const scope = filters?.locationScope || 'both_or';
      const contactClauses = [
        `contact_location_full.ilike.%${locationTerm}%`,
        `contact_city.ilike.%${locationTerm}%`,
        `contact_state.ilike.%${locationTerm}%`,
        `contact_country.ilike.%${locationTerm}%`,
      ];
      const companyClauses = [
        `company_location_full.ilike.%${locationTerm}%`,
        `company_city.ilike.%${locationTerm}%`,
        `company_state.ilike.%${locationTerm}%`,
        `company_country.ilike.%${locationTerm}%`,
      ];
      if (scope === 'contact') {
        query = query.or(contactClauses.join(','));
      } else if (scope === 'company') {
        query = query.or(companyClauses.join(','));
      } else if (scope === 'both_and') {
        query = query.or(contactClauses.join(','));
        query = query.or(companyClauses.join(','));
      } else {
        query = query.or([...contactClauses, ...companyClauses].join(','));
      }
    }

    const from = Math.max(0, page * perPage);
    const to = from + Math.max(1, perPage) - 1;
    const ascending = String(sortDir || '').toLowerCase() === 'asc';
    const sortColumn = sortCol || 'enriched_at';
    const { data, error, count } = await query.order(sortColumn, { ascending }).range(from, to);
    if (error) {
      console.error('Supabase advanced contacts query failed:', error);
      throw new Error(error.message);
    }
    const mapped = (data || []).map((r: any) => mapSupabaseContactRow(r));
    return { contacts: mapped, totalCount: options?.includeCount === false ? mapped.length : Number(count ?? 0) };
  }

  const rpcParams: any = {
    p_user_email: userEmail,
    p_page: page,
    p_per_page: perPage,
    p_sort_col: sortCol,
    p_sort_dir: sortDir,
  };
  if (search) rpcParams.p_search = search;
  if (dateFrom) rpcParams.p_date_from = dateFrom;
  if (dateTo) rpcParams.p_date_to = dateTo + 'T23:59:59.999Z';

  const { data, error } = await supabase.rpc('get_contacts_page_v2', rpcParams);

  if (error) {
    console.error('Supabase RPC failed:', error);
    throw new Error(error.message);
  }

  const rows = (data || []) as any[];

  const includeCount = options?.includeCount !== false;
  let totalCount = 0;
  if (includeCount) {
    const countKey = contactsCountCacheKey({ userEmail, search, dateFrom, dateTo });
    const countParams: any = {
      p_user_email: userEmail,
    };
    if (search) countParams.p_search = search;
    if (dateFrom) countParams.p_date_from = dateFrom;
    if (dateTo) countParams.p_date_to = dateTo + 'T23:59:59.999Z';

    const { data: countData, error: countError } = await supabase.rpc('get_contacts_count_v2', countParams);

    if (countError) {
      console.error('Supabase contacts count RPC failed:', countError);
      const cachedCount = contactsCountCache.get(countKey);
      if (Number.isFinite(cachedCount)) {
        totalCount = Number(cachedCount);
      } else {
        // Preserve pagination shape on first failure instead of collapsing to one page.
        totalCount = Math.max(page * perPage + rows.length, rows.length);
      }
    } else {
      totalCount = Number(countData ?? 0);
      if (Number.isFinite(totalCount)) {
        contactsCountCache.set(countKey, totalCount);
      }
    }
  }

  const mapped = rows.map((r: any) => mapSupabaseContactRow(r));

  return { contacts: mapped, totalCount: includeCount ? totalCount : mapped.length };
}

export async function updateContactListsInSupabase(params: {
  userEmail: string;
  searchResultIds: string[];
  listName: string;
  action: 'add' | 'remove';
}): Promise<{ updated: number }> {
  const userEmail = String(params.userEmail || '').trim().toLowerCase();
  const listName = String(params.listName || '').trim();
  const action = params.action;
  const uniqueIds = Array.from(
    new Set(
      (params.searchResultIds || [])
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    )
  );
  if (!userEmail || !listName || (action !== 'add' && action !== 'remove') || uniqueIds.length === 0) {
    return { updated: 0 };
  }

  let updated = 0;
  const CHUNK_SIZE = 250;
  for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
    const chunk = uniqueIds.slice(i, i + CHUNK_SIZE);
    const { data: rows, error: rowsError } = await supabase
      .from('seamless_data')
      .select('search_result_id,tags')
      .eq('user_email', userEmail)
      .in('search_result_id', chunk);

    if (rowsError) {
      throw new Error(rowsError.message);
    }

    for (const row of rows || []) {
      const searchResultId = String((row as any)?.search_result_id || '').trim();
      if (!searchResultId) continue;
      const currentTags = normalizeTagArray((row as any)?.tags);
      let nextTags: string[] = currentTags;

      if (action === 'add') {
        if (currentTags.includes(listName)) continue;
        nextTags = [...currentTags, listName];
      } else {
        if (!currentTags.includes(listName)) continue;
        nextTags = currentTags.filter((tag) => tag !== listName);
      }

      const { error: updateError } = await supabase
        .from('seamless_data')
        .update({
          tags: nextTags,
          updated_at: new Date().toISOString(),
        })
        .eq('user_email', userEmail)
        .eq('search_result_id', searchResultId);

      if (updateError) {
        throw new Error(updateError.message);
      }
      updated += 1;
    }
  }

  return { updated };
}

export async function loadCompaniesServerSide(
  userEmail: string,
  page: number = 0,
  perPage: number = 25,
  sortCol: string = 'company',
  sortDir: string = 'asc',
  search: string = '',
): Promise<{ companies: any[]; totalCount: number }> {

  const { data, error } = await supabase.rpc('get_companies', {
    p_user_email: userEmail,
    p_page: page,
    p_per_page: perPage,
    p_sort_col: sortCol,
    p_sort_dir: sortDir,
    ...(search ? { p_search: search } : {}),
  });

  if (error) {
    console.error('Supabase companies query failed:', error);
    throw new Error(error.message);
  }

  const rows = (Array.isArray(data) ? data : []) as any[];

  // Get total company count
  const { data: countData } = await supabase.rpc('get_companies', {
    p_user_email: userEmail,
    p_page: 0,
    p_per_page: 999999,
    ...(search ? { p_search: search } : {}),
  });
  const totalCount = Array.isArray(countData) ? countData.length : 0;

  return { companies: rows, totalCount };
}
